/**
 * Builder to Simple JSON Transformer
 *
 * Converts complex Builder JSON format back to the simplified AI-friendly format.
 * This is the reverse of simple-to-builder.ts
 *
 * Use cases:
 * 1. AI editing - Convert existing docs to simple format for AI modification
 * 2. Migration - Convert existing complex documents to simpler format
 * 3. Debugging - Understand what's in a complex document
 */

import type {
  SimpleCommonBlockStyle,
  SimpleDocument,
  SimpleFillableChildItem,
  SimpleFillableTextContent,
  SimpleInlineFieldType,
  SimpleLayoutImage,
  SimplePage,
  SimplePageAppearance,
  SimplePageCardAppearance,
  SimplePageHeaderFooterElement,
  SimplePageHeaderFooterPreserve,
  SimpleItem,
  SimpleListItem,
  SimpleParagraphTextStyle,
  SimpleRichContent,
  SimpleSmartBlockItem,
  SimpleSmartTextContent,
  SimpleSmartTextItem,
  SimpleContentBlockType,
  SimplePricingColumnRole,
  SimpleTableCellFormat,
  SimpleTableEmbeddedBlock,
  SimpleTextChildItem,
  SimpleTextContent,
  SimpleTextStyle,
} from './simple-builder-schema';

import {
  CONTENT_BLOCK_TYPES,
  SLATE_TO_SIMPLE_MAP,
  DEFAULT_SPACE_HEIGHT,
  SMART_BLOCK_DEFAULTS,
  getBlockData,
  getBlockItems,
} from './transformer-constants';
import { completeSemanticPricingTable } from './pricing-table-repair';
import { themeColorTokenFromCss } from './theme-color-tokens';

// ============================================================================
// SLATE.JS TEXT EXTRACTION
// ============================================================================

interface SlateNode {
  type?: string;
  text?: string;
  children?: SlateNode[];
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  backgroundColor?: string;
  fontSize?: string | number;
  url?: string;
  newTab?: boolean;
  align?: 'left' | 'center' | 'right' | 'justify';
  listStyle?: string;
  textStyle?: SimpleParagraphTextStyle;
  indentLevel?: number;
  restartNumbering?: boolean;
  listVariant?: string;
  data?: Record<string, unknown>;
  // Mention properties
  character?: string;
  variable?: { key: string; value?: string; type?: 'system' | 'custom'; isOverride?: boolean };
  fieldType?: string;
  fieldId?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
  options?: Array<{ label: string; value: string }>;
  multiSelect?: boolean;
  min?: string | number;
  max?: string | number;
  rows?: number;
  maxLength?: number;
}

const DEFAULT_PAGE_NAME = 'Untitled Page';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isGradientString(value: string): boolean {
  return value.includes('gradient(');
}

function inferFillMode(value: string): 'solid' | 'gradient' {
  return isGradientString(value) ? 'gradient' : 'solid';
}

function inferCardMode(value: string): 'solid' | 'gradient' {
  return isGradientString(value) ? 'gradient' : 'solid';
}

function extractLayoutImage(cardBg: Record<string, unknown>): SimpleLayoutImage | undefined {
  const generation = pickDefined({
    image_generation_request_id: isNonEmptyString(cardBg.image_generation_request_id)
      ? cardBg.image_generation_request_id
      : undefined,
    image_generation_status: isNonEmptyString(cardBg.image_generation_status)
      ? cardBg.image_generation_status
      : undefined,
    image_generation_error: isNonEmptyString(cardBg.image_generation_error)
      ? cardBg.image_generation_error
      : undefined,
  });

  if (isNonEmptyString(cardBg.imageUrl)) {
    return {
      url: cardBg.imageUrl,
      prompt: isNonEmptyString(cardBg.image_prompt) ? cardBg.image_prompt : undefined,
      ...generation,
    };
  }

  if (isNonEmptyString(cardBg.image_prompt)) {
    return Object.keys(generation).length > 0
      ? { prompt: cardBg.image_prompt, ...generation }
      : cardBg.image_prompt;
  }

  return undefined;
}

function extractCardAppearance(
  cardBg: Record<string, unknown>,
): SimplePageCardAppearance | undefined {
  if (cardBg.useThemeCardColor === true) {
    return { mode: 'theme' };
  }

  const colorCode = isNonEmptyString(cardBg.colorCode)
    ? cardBg.colorCode
    : isNonEmptyString(cardBg.backgroundColor)
      ? cardBg.backgroundColor
      : undefined;

  if (!colorCode) {
    return undefined;
  }

  return {
    mode: inferCardMode(colorCode),
    value: themeColorTokenFromCss(colorCode) || colorCode,
  };
}

function extractHeaderFooterPreserve(
  headerFooter: unknown,
): SimplePageHeaderFooterPreserve | undefined {
  if (!headerFooter || typeof headerFooter !== 'object' || Array.isArray(headerFooter)) {
    return undefined;
  }

  const headerFooterRecord = headerFooter as Record<string, unknown>;
  const preserve: SimplePageHeaderFooterPreserve = {};

  if (typeof headerFooterRecord.hideForThisSection === 'boolean') {
    preserve.hideForThisSection = headerFooterRecord.hideForThisSection;
  }

  const override = headerFooterRecord.override;
  if (override && typeof override === 'object' && !Array.isArray(override)) {
    const overrideRecord = override as Record<string, unknown>;
    if (Array.isArray(overrideRecord.elements)) {
      const elements = overrideRecord.elements
        .filter(
          (element): element is Record<string, unknown> => !!element && typeof element === 'object',
        )
        .map(
          (element): SimplePageHeaderFooterElement => ({
            ...(isNonEmptyString(element.id) ? { id: element.id } : {}),
            slot: (element.slot as SimplePageHeaderFooterElement['slot']) || 'top-left',
            type: (element.type as SimplePageHeaderFooterElement['type']) || 'text',
            ...(typeof element.value === 'string' ? { value: element.value } : {}),
            ...(typeof element.imageUrl === 'string' ? { imageUrl: element.imageUrl } : {}),
            ...(typeof element.enabled === 'boolean' ? { enabled: element.enabled } : {}),
            ...(typeof element.width === 'number' ? { width: element.width } : {}),
            ...(typeof element.height === 'number' ? { height: element.height } : {}),
          }),
        );

      if (elements.length > 0) {
        preserve.override = { elements };
      }
    }
  }

  return Object.keys(preserve).length > 0 ? preserve : undefined;
}

/**
 * Extract plain text from Slate.js content
 * Converts mention nodes back to [[variable.key]] syntax
 */
function slateToText(content: SlateNode[] | undefined): string {
  if (!content || !Array.isArray(content)) return '';

  return content
    .map((node) => {
      // Handle mention nodes - convert back to [[variable]] syntax
      if (node.type === 'mention' && node.variable?.key) {
        return `[[${node.variable.key}]]`;
      }
      if (node.text !== undefined) {
        return node.text;
      }
      if (node.children) {
        return slateToText(node.children);
      }
      return '';
    })
    .join('')
    .trim();
}

function slateToTextWithVoids(content: SlateNode[]): string {
  return content
    .map((node) => {
      const d = (node as any).data;
      if (node.type === 'table-cell-image') return `[image: ${d?.alt || d?.image || ''}]`;
      if (node.type === 'table-cell-video') return `[video: ${d?.video || ''}]`;
      if (node.type === 'table-cell-button') return `[button: ${d?.label || ''}]`;
      if (node.type === 'mention' && node.variable?.key) return `[[${node.variable.key}]]`;
      if (node.text !== undefined) return node.text;
      if (node.children) return slateToTextWithVoids(node.children);
      return '';
    })
    .join('')
    .trim();
}

function isSlateContent(value: unknown): value is SlateNode[] {
  if (!Array.isArray(value)) return false;
  if (value.length === 0) return true;
  return value.every(
    (node) => typeof node === 'object' && node !== null && ('children' in node || 'text' in node),
  );
}

function tableCellToSimpleText(cell: any): string {
  if (typeof cell === 'string') return cell;
  if (typeof cell === 'number' || typeof cell === 'boolean') return String(cell);

  if (isSlateContent(cell)) {
    return slateToTextWithVoids(cell);
  }

  if (cell && typeof cell === 'object') {
    if (isSlateContent(cell.content)) {
      return slateToText(cell.content);
    }

    if (cell.kind === 'table_cell_block') {
      const blockType = cell.blockType;
      const blockData = cell.data || {};

      if (isSlateContent(blockData.content)) {
        return slateToText(blockData.content);
      }

      if (blockType === 'button') {
        return String(blockData.label || blockData.url || '').trim();
      }

      if (blockType === 'image') {
        return String(blockData.alt || blockData.image || '').trim();
      }

      if (blockType === 'video' || blockType === 'embed_webpage') {
        return String(blockData.title || blockData.video || '').trim();
      }

      return '';
    }

    if (typeof cell.value === 'string') {
      return cell.value;
    }
  }

  return '';
}

/**
 * Extract bullet/numbered list items from Slate.js content
 */
function justifyContentToAlignment(
  justifyContent?: string,
): 'left' | 'center' | 'right' | 'justify' | undefined {
  if (!justifyContent) return undefined;
  if (justifyContent === 'right' || justifyContent === 'flex-end') return 'right';
  if (justifyContent === 'center') return 'center';
  if (justifyContent === 'justify') return 'justify';
  return 'left';
}

function blockStyleAlignment(style: any): 'left' | 'center' | 'right' | 'justify' | undefined {
  if (!style || typeof style !== 'object') return undefined;

  if (
    style.textAlign === 'left' ||
    style.textAlign === 'center' ||
    style.textAlign === 'right' ||
    style.textAlign === 'justify'
  ) {
    return style.textAlign;
  }

  return justifyContentToAlignment(style.justifyContent);
}

const DEFAULT_CHART_STYLE = { theme: 'default', width: 600, height: 400 };

function nonEmptyRecord<T extends Record<string, unknown>>(value: T | undefined): T | undefined {
  return value && Object.keys(value).length > 0 ? value : undefined;
}

function pickDefined<T extends Record<string, any>>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as Partial<T>;
}

function extractCommonBlockStyle(style: any): SimpleCommonBlockStyle | undefined {
  if (!style || typeof style !== 'object') return undefined;

  const normalized = pickDefined({
    align: justifyContentToAlignment(style.justifyContent),
    color: typeof style.color === 'string' ? style.color : undefined,
    backgroundColor: typeof style.backgroundColor === 'string' ? style.backgroundColor : undefined,
    borderColor: typeof style.borderColor === 'string' ? style.borderColor : undefined,
    borderWidth: typeof style.borderWidth === 'number' ? style.borderWidth : undefined,
    borderRadius: typeof style.borderRadius === 'number' ? style.borderRadius : undefined,
    width: style.width,
    height: typeof style.height === 'number' ? style.height : undefined,
  });

  return Object.keys(normalized).length > 0 ? (normalized as SimpleCommonBlockStyle) : undefined;
}

function extractNonAlignmentBlockStyle(style: any): SimpleCommonBlockStyle | undefined {
  const normalized = extractCommonBlockStyle(style);
  if (!normalized) return undefined;

  const { align: _align, ...rest } = normalized;
  return Object.keys(rest).length > 0 ? (rest as SimpleCommonBlockStyle) : undefined;
}

function extractTextBlockStyle(data: any): SimpleTextStyle | undefined {
  const normalized = pickDefined({
    fontSize:
      typeof data?.style?.fontSize === 'string' || typeof data?.style?.fontSize === 'number'
        ? String(data.style.fontSize)
        : undefined,
    color: typeof data?.style?.color === 'string' ? data.style.color : undefined,
    backgroundColor:
      typeof data?.style?.backgroundColor === 'string' ? data.style.backgroundColor : undefined,
    align: justifyContentToAlignment(data?.style?.justifyContent),
  });

  return Object.keys(normalized).length > 0 ? (normalized as SimpleTextStyle) : undefined;
}

function slateNodeToRich(node: SlateNode): any {
  if (node.text !== undefined) {
    return pickDefined({
      text: node.text,
      bold: node.bold || undefined,
      italic: node.italic || undefined,
      underline: node.underline || undefined,
      code: node.code || undefined,
      color: node.color || undefined,
      fontSize: node.fontSize,
    });
  }

  return pickDefined({
    type: node.type,
    children: Array.isArray(node.children)
      ? node.children.map((child) => slateNodeToRich(child))
      : [],
    url: node.url || undefined,
    newTab: node.newTab || undefined,
    align: node.align || undefined,
    listStyle: node.listStyle || undefined,
    textStyle: node.textStyle || undefined,
    indentLevel: node.indentLevel || undefined,
    restartNumbering: node.restartNumbering || undefined,
    listVariant:
      node.listVariant && node.listVariant !== 'paragraph' ? node.listVariant : undefined,
    character: node.character || undefined,
    variable: node.variable || undefined,
    fieldType: node.fieldType || undefined,
    fieldId: node.fieldId || undefined,
    placeholder: node.placeholder || undefined,
    value: typeof node.value === 'string' ? node.value : undefined,
    required: typeof node.required === 'boolean' ? node.required : undefined,
    options: Array.isArray(node.options) ? node.options : undefined,
    data: node.data || undefined,
  });
}

function slateContentToRich(content: SlateNode[]): SimpleRichContent {
  return content.map((node) => slateNodeToRich(node)) as SimpleRichContent;
}

function pickInlineTextMarks(node: {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}) {
  return pickDefined({
    bold: node.bold || undefined,
    italic: node.italic || undefined,
    underline: node.underline || undefined,
    code: node.code || undefined,
    color: node.color || undefined,
    fontSize: node.fontSize,
  });
}

function getMentionMarks(node: SlateNode) {
  const firstChild = Array.isArray(node.children) ? node.children[0] : undefined;
  return {
    ...pickInlineTextMarks(node),
    ...(firstChild ? pickInlineTextMarks(firstChild) : {}),
  };
}

function slateInlineNodeToSimpleText(node: SlateNode): SimpleTextContent {
  if (node.type === 'mention' && node.variable?.key) {
    return [
      {
        var: node.variable.key,
        ...(node.variable.isOverride
          ? { override: node.variable.value ?? node.character ?? '' }
          : {}),
        ...getMentionMarks(node),
      },
    ];
  }

  if (node.text !== undefined) {
    return [
      {
        text: node.text,
        ...pickInlineTextMarks(node),
      },
    ];
  }

  if (node.type === 'link' && node.url) {
    return [
      {
        link: node.url,
        ...(node.newTab ? { newTab: true } : {}),
        content: slateChildrenToSimpleText(node.children),
      },
    ];
  }

  if (Array.isArray(node.children)) {
    return slateChildrenToSimpleText(node.children);
  }

  return [];
}

function slateChildrenToSimpleText(children: SlateNode[] | undefined): SimpleTextContent {
  if (!Array.isArray(children)) return [];
  return children.flatMap((child) => slateInlineNodeToSimpleText(child));
}

function slateInlineFieldToSimpleNode(node: SlateNode): SimpleFillableTextContent[number] {
  const fieldType: SimpleInlineFieldType =
    node.fieldType === 'name' ||
    node.fieldType === 'email' ||
    node.fieldType === 'date' ||
    node.fieldType === 'number' ||
    node.fieldType === 'checkbox' ||
    node.fieldType === 'dropdown' ||
    node.fieldType === 'textarea'
      ? node.fieldType
      : 'text_input';

  const field = {
    type: fieldType,
    ...(node.placeholder ? { placeholder: node.placeholder } : {}),
    ...(typeof node.required === 'boolean' ? { required: node.required } : {}),
    ...(Array.isArray(node.options) ? { options: node.options } : {}),
    ...(typeof node.multiSelect === 'boolean' ? { multiSelect: node.multiSelect } : {}),
    ...(node.min != null ? { min: node.min } : {}),
    ...(node.max != null ? { max: node.max } : {}),
    ...(typeof node.rows === 'number' ? { rows: node.rows } : {}),
    ...(typeof node.maxLength === 'number' ? { maxLength: node.maxLength } : {}),
  };

  return {
    field,
  };
}

function slateInlineNodeToSimpleFillableText(node: SlateNode): SimpleFillableTextContent {
  if (node.type === 'inline_field') {
    return [slateInlineFieldToSimpleNode(node)];
  }

  return slateInlineNodeToSimpleText(node) as SimpleFillableTextContent;
}

function slateChildrenToSimpleFillableText(
  children: SlateNode[] | undefined,
): SimpleFillableTextContent {
  if (!Array.isArray(children)) return [];
  return children.flatMap((child) => slateInlineNodeToSimpleFillableText(child));
}

function isSimpleInlineChildren(children: SlateNode[] | undefined): boolean {
  if (!Array.isArray(children)) return false;
  return children.every((child) => {
    if (child.text !== undefined) return true;
    if (child.type === 'mention' && child.variable?.key) return Array.isArray(child.children);
    if (child.type === 'link' && child.url) return isSimpleInlineChildren(child.children);
    return false;
  });
}

function isSimpleFillableInlineChildren(children: SlateNode[] | undefined): boolean {
  if (!Array.isArray(children)) return false;
  return children.every((child) => {
    if (child.text !== undefined) return true;
    if (child.type === 'mention' && child.variable?.key) return Array.isArray(child.children);
    if (child.type === 'link' && child.url) return isSimpleInlineChildren(child.children);
    if (child.type === 'inline_field') return Array.isArray(child.children);
    return false;
  });
}

function isSimpleListNode(
  node: SlateNode,
  type: 'bulleted-list' | 'numbered-list' | 'todo-list',
): boolean {
  if (node.type !== type) return false;
  if (!Array.isArray(node.children)) return false;

  return node.children.every(
    (child) => child.type === 'list-item' && isSimpleInlineChildren(child.children),
  );
}

/** Wraps an item's content with its indent, numbering restart, alignment and variant when it has any. */
function toSimpleListItem<Content>(child: SlateNode, content: Content): SimpleListItem<Content> {
  const extras = pickDefined({
    indent: child.indentLevel || undefined,
    restart: child.restartNumbering || undefined,
    align: child.align,
    variant: child.listVariant && child.listVariant !== 'paragraph' ? child.listVariant : undefined,
  });

  return Object.keys(extras).length > 0 ? { content, ...extras } : content;
}

function listItemContent(item: SimpleListItem | undefined): SimpleTextContent | undefined {
  return Array.isArray(item) ? item : item?.content;
}

function slateListNodeToSimpleTextItems(node: SlateNode): SimpleListItem[] {
  if (!Array.isArray(node.children)) return [];

  return node.children
    .filter((child) => child.type === 'list-item' && isSimpleInlineChildren(child.children))
    .flatMap((child) => {
      const content = slateChildrenToSimpleText(child.children);
      return content.length > 0 ? [toSimpleListItem(child, content)] : [];
    });
}

function isSimpleFillableListNode(
  node: SlateNode,
  type: 'bulleted-list' | 'numbered-list',
): boolean {
  if (node.type !== type) return false;
  if (!Array.isArray(node.children)) return false;

  return node.children.every(
    (child) => child.type === 'list-item' && isSimpleFillableInlineChildren(child.children),
  );
}

function slateListNodeToSimpleFillableTextItems(
  node: SlateNode,
): SimpleListItem<SimpleFillableTextContent>[] {
  if (!Array.isArray(node.children)) return [];

  return node.children
    .filter((child) => child.type === 'list-item' && isSimpleFillableInlineChildren(child.children))
    .flatMap((child) => {
      const content = slateChildrenToSimpleFillableText(child.children);
      return content.length > 0 ? [toSimpleListItem(child, content)] : [];
    });
}

function appendInlineSeparator(content: SimpleTextContent): SimpleTextContent {
  if (content.length === 0) return [];
  return [...content, { text: '\n' }];
}

function slateValueToSimpleTextContent(value: unknown): SimpleTextContent {
  if (typeof value === 'string') {
    return value ? [{ text: value }] : [];
  }

  if (!isSlateContent(value)) return [];

  return value.reduce<SimpleTextContent>((accumulator, node, index) => {
    const inlineContent =
      node.text !== undefined || node.type === 'mention' || node.type === 'link'
        ? slateInlineNodeToSimpleText(node)
        : slateChildrenToSimpleText(node.children);

    if (inlineContent.length === 0) {
      return accumulator;
    }

    if (index === 0) {
      return [...inlineContent];
    }

    return [...appendInlineSeparator(accumulator), ...inlineContent];
  }, []);
}

function findFirstTextLeaf(node: SlateNode | undefined): SlateNode | null {
  if (!node) return null;
  if (node.text !== undefined) return node;
  if (!Array.isArray(node.children)) return null;

  for (const child of node.children) {
    const match = findFirstTextLeaf(child);
    if (match) return match;
  }

  return null;
}

/** The marks of every run the reader sees: non-empty text leaves and mentions. */
function collectVisibleRuns(node: SlateNode, runs: SlateNode[] = []): SlateNode[] {
  if (node.type === 'mention') {
    runs.push({ ...node.children?.[0], ...node });
  } else if (node.text !== undefined) {
    if (node.text !== '') runs.push(node);
  } else {
    node.children?.forEach((child) => collectVisibleRuns(child, runs));
  }
  return runs;
}

/**
 * A node-level value is only safe to hoist when every run carries it. Hoisting the first
 * run's value onto a node whose later runs have none or another value restyles those runs.
 */
function uniformRunValue(runs: SlateNode[], key: 'fontSize' | 'color' | 'backgroundColor') {
  const [first] = runs;
  if (!first || runs.some((run) => run[key] !== first[key])) return undefined;
  return first[key];
}

function extractSlateNodeTextStyle(node: SlateNode): SimpleTextStyle | undefined {
  const visibleRuns = collectVisibleRuns(node);
  const runs = visibleRuns.length > 0 ? visibleRuns : [findFirstTextLeaf(node) ?? {}];
  const fontSize = uniformRunValue(runs, 'fontSize');
  const color = uniformRunValue(runs, 'color');
  const backgroundColor = uniformRunValue(runs, 'backgroundColor');

  const normalized = pickDefined({
    fontSize:
      typeof fontSize === 'string' || typeof fontSize === 'number' ? String(fontSize) : undefined,
    color: typeof color === 'string' ? color : undefined,
    backgroundColor: typeof backgroundColor === 'string' ? backgroundColor : undefined,
    align: node.align,
    listStyle: typeof node.listStyle === 'string' ? node.listStyle : undefined,
    textStyle: node.textStyle,
  });

  return Object.keys(normalized).length > 0 ? (normalized as SimpleTextStyle) : undefined;
}

function mergeTextStyles(
  ...styles: Array<SimpleTextStyle | undefined>
): SimpleTextStyle | undefined {
  const normalized = pickDefined(
    styles.reduce<Record<string, unknown>>(
      (merged, style) => ({ ...merged, ...(style || {}) }),
      {},
    ),
  );

  return Object.keys(normalized).length > 0 ? (normalized as SimpleTextStyle) : undefined;
}

function subtractTextStyle(
  style?: SimpleTextStyle,
  baseStyle?: SimpleTextStyle,
): SimpleTextStyle | undefined {
  if (!style) return undefined;

  const normalized = pickDefined({
    fontSize: style.fontSize !== baseStyle?.fontSize ? style.fontSize : undefined,
    color: style.color !== baseStyle?.color ? style.color : undefined,
    backgroundColor:
      style.backgroundColor !== baseStyle?.backgroundColor ? style.backgroundColor : undefined,
    align: style.align !== baseStyle?.align ? style.align : undefined,
    listStyle: style.listStyle !== baseStyle?.listStyle ? style.listStyle : undefined,
    textStyle: style.textStyle !== baseStyle?.textStyle ? style.textStyle : undefined,
  });

  return Object.keys(normalized).length > 0 ? (normalized as SimpleTextStyle) : undefined;
}

function slateNodeToSmartTextItem(node: SlateNode): SimpleSmartTextItem | null {
  if (node.type === 'bulleted-list' && isSimpleListNode(node, 'bulleted-list')) {
    const items = slateListNodeToSimpleTextItems(node);
    return items.length > 0
      ? (applyTextBlockStyle(
          { bullets: items },
          extractSlateNodeTextStyle(node),
        ) as SimpleSmartTextItem)
      : null;
  }

  if (node.type === 'numbered-list' && isSimpleListNode(node, 'numbered-list')) {
    const items = slateListNodeToSimpleTextItems(node);
    return items.length > 0
      ? (applyTextBlockStyle(
          { numbered: items },
          extractSlateNodeTextStyle(node),
        ) as SimpleSmartTextItem)
      : null;
  }

  const style = extractSlateNodeTextStyle(node);
  const simpleItem = nodeToSimpleTextItem(node, style);

  if (!simpleItem) {
    return null;
  }

  if (
    'block_quote' in simpleItem ||
    'h1' in simpleItem ||
    'h2' in simpleItem ||
    'h3' in simpleItem ||
    'h4' in simpleItem ||
    'h5' in simpleItem ||
    'p' in simpleItem
  ) {
    return simpleItem as SimpleSmartTextItem;
  }

  return null;
}

function slateNodeToTextChildItem(
  node: SlateNode,
  baseStyle?: SimpleTextStyle,
): SimpleTextChildItem | null {
  if (node.type === 'bulleted-list' && isSimpleListNode(node, 'bulleted-list')) {
    const items = slateListNodeToSimpleTextItems(node);
    const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
    return items.length > 0
      ? ({ bullets: items, ...(style ? { style } : {}) } as SimpleTextChildItem)
      : null;
  }

  if (node.type === 'numbered-list' && isSimpleListNode(node, 'numbered-list')) {
    const items = slateListNodeToSimpleTextItems(node);
    const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
    return items.length > 0
      ? ({ numbered: items, ...(style ? { style } : {}) } as SimpleTextChildItem)
      : null;
  }

  const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
  const simpleItem = nodeToSimpleTextItem(node, style);

  if (!simpleItem) {
    return null;
  }

  if (
    'block_quote' in simpleItem ||
    'h1' in simpleItem ||
    'h2' in simpleItem ||
    'h3' in simpleItem ||
    'h4' in simpleItem ||
    'h5' in simpleItem ||
    'p' in simpleItem
  ) {
    return simpleItem as SimpleTextChildItem;
  }

  return null;
}

function slateNodeToFillableChildItem(
  node: SlateNode,
  baseStyle?: SimpleTextStyle,
): SimpleFillableChildItem | null {
  if (node.type === 'bulleted-list' && isSimpleFillableListNode(node, 'bulleted-list')) {
    const items = slateListNodeToSimpleFillableTextItems(node);
    const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
    return items.length > 0
      ? ({ bullets: items, ...(style ? { style } : {}) } as SimpleFillableChildItem)
      : null;
  }

  if (node.type === 'numbered-list' && isSimpleFillableListNode(node, 'numbered-list')) {
    const items = slateListNodeToSimpleFillableTextItems(node);
    const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
    return items.length > 0
      ? ({ numbered: items, ...(style ? { style } : {}) } as SimpleFillableChildItem)
      : null;
  }

  const style = subtractTextStyle(extractSlateNodeTextStyle(node), baseStyle);
  return nodeToSimpleFillableChildItem(node, style);
}

function slateValueToSmartTextContent(value: unknown): SimpleSmartTextContent {
  if (typeof value === 'string') {
    return value ? [{ text: value }] : [];
  }

  if (!isSlateContent(value)) return [];
  const nodes = repairTextNodes(value);
  const firstNode = nodes[0];

  if (
    nodes.length === 1 &&
    firstNode &&
    (!firstNode.type || firstNode.type === 'paragraph') &&
    isSimpleInlineChildren(firstNode.children) &&
    !firstNode.align
  ) {
    return slateChildrenToSimpleText(firstNode.children);
  }

  const blocks = nodes
    .map((node) => slateNodeToSmartTextItem(node))
    .filter((item): item is SimpleSmartTextItem => item !== null);

  return blocks.length > 0 ? blocks : slateValueToSimpleTextContent(nodes);
}

function nodeToSimpleTextItem(node: SlateNode, style?: SimpleTextStyle): SimpleItem | null {
  if (!isSimpleInlineChildren(node.children)) return null;
  const nodeType = node.type || 'paragraph';

  if (nodeType === 'paragraph') {
    return applyTextBlockStyle({ p: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'heading-one') {
    return applyTextBlockStyle({ h1: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'heading-two') {
    return applyTextBlockStyle({ h2: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'heading-three') {
    return applyTextBlockStyle({ h3: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'heading-four') {
    return applyTextBlockStyle({ h4: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'heading-five') {
    return applyTextBlockStyle({ h5: slateChildrenToSimpleText(node.children) }, style);
  }

  if (nodeType === 'block-quote') {
    return {
      block_quote: {
        content: slateChildrenToSimpleText(node.children),
        ...(style ? { style } : {}),
      },
    };
  }

  return null;
}

function nodeToSimpleFillableChildItem(
  node: SlateNode,
  style?: SimpleTextStyle,
): SimpleFillableChildItem | null {
  if (!isSimpleFillableInlineChildren(node.children)) return null;
  const nodeType = node.type || 'paragraph';

  if (nodeType === 'paragraph') {
    return applyTextBlockStyle(
      { p: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'heading-one') {
    return applyTextBlockStyle(
      { h1: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'heading-two') {
    return applyTextBlockStyle(
      { h2: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'heading-three') {
    return applyTextBlockStyle(
      { h3: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'heading-four') {
    return applyTextBlockStyle(
      { h4: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'heading-five') {
    return applyTextBlockStyle(
      { h5: slateChildrenToSimpleFillableText(node.children) } as SimpleItem,
      style,
    ) as SimpleFillableChildItem;
  }

  if (nodeType === 'block-quote') {
    return {
      block_quote: {
        content: slateChildrenToSimpleFillableText(node.children),
        ...(style ? { style } : {}),
      },
    };
  }

  return null;
}

// ============================================================================
// BLOCK TRANSFORMERS
// ============================================================================

/**
 * Transform text block to simple format
 */
function applyTextBlockStyle(item: SimpleItem, style?: SimpleTextStyle): SimpleItem {
  if (!style || Object.keys(style).length === 0) return item;
  return {
    ...item,
    style,
  } as SimpleItem;
}

const SLATE_LIST_TYPES = new Set(['bulleted-list', 'numbered-list', 'todo-list']);
const SLATE_TEXT_ELEMENT_TYPES = new Set([
  'paragraph',
  'heading-one',
  'heading-two',
  'heading-three',
  'heading-four',
  'heading-five',
  'block-quote',
]);

/** Keeps the inline runs of block-level children, joined by line breaks, so no text is lost. */
function flattenToInlineNodes(children: SlateNode[] | undefined): SlateNode[] {
  const inline: SlateNode[] = [];

  children?.forEach((child) => {
    if (child.text !== undefined) {
      inline.push(child);
    } else if (child.type === 'mention') {
      if (child.variable?.key) inline.push(child);
      else if (child.character) inline.push({ text: child.character });
    } else if (child.type === 'link' && child.url) {
      inline.push({ ...child, children: flattenToInlineNodes(child.children) });
    } else {
      const nested = flattenToInlineNodes(child.children);
      if (nested.length > 0) {
        if (inline.length > 0) inline.push({ text: '\n' });
        inline.push(...nested);
      }
    }
  });

  return inline;
}

/** Rebuilds a list so every child is a list-item of inline content; nested lists become indented items. */
function repairListNode(list: SlateNode): SlateNode {
  const items: SlateNode[] = [];

  const collect = (children: SlateNode[] | undefined, level: number) => {
    children?.forEach((child) => {
      if (SLATE_LIST_TYPES.has(child.type ?? '')) {
        collect(child.children, level + 1);
        return;
      }

      const ownChildren = child.type === 'list-item' ? child.children : [child];
      const inline = flattenToInlineNodes(
        ownChildren?.filter((c) => !SLATE_LIST_TYPES.has(c.type ?? '')),
      );
      const indentLevel = (child.indentLevel ?? 0) + level;
      items.push({
        ...(child.type === 'list-item' ? child : { type: 'list-item' }),
        children: inline.length > 0 ? inline : [{ text: '' }],
        ...(indentLevel > 0 ? { indentLevel } : {}),
      });
      collect(
        ownChildren?.filter((c) => SLATE_LIST_TYPES.has(c.type ?? '')),
        level + 1,
      );
    });
  };

  collect(list.children, 0);
  return { ...list, children: items };
}

/**
 * Brings Slate content that the editor can hold but Simple JSON cannot express into the nearest
 * valid shape: list-items outside a list, lists inside lists, block elements inside paragraphs
 * and element types such as heading-six. The text stays; only the structure is normalised.
 */
function repairTextNodes(content: SlateNode[]): SlateNode[] {
  const repaired: SlateNode[] = [];
  let orphanItems: SlateNode[] = [];

  const flushOrphans = () => {
    if (orphanItems.length > 0) {
      repaired.push(repairListNode({ type: 'bulleted-list', children: orphanItems }));
      orphanItems = [];
    }
  };

  content.forEach((node) => {
    if (node.type === 'list-item') {
      orphanItems.push(node);
      return;
    }
    flushOrphans();

    if (SLATE_LIST_TYPES.has(node.type ?? '')) {
      repaired.push(repairListNode(node));
    } else if (node.text !== undefined && !node.children) {
      repaired.push(node);
    } else if (
      isSimpleInlineChildren(node.children) &&
      SLATE_TEXT_ELEMENT_TYPES.has(node.type ?? '')
    ) {
      repaired.push(node);
    } else {
      repaired.push({
        ...node,
        type: SLATE_TEXT_ELEMENT_TYPES.has(node.type ?? '') ? node.type : 'paragraph',
        children: flattenToInlineNodes(node.children),
      });
    }
  });
  flushOrphans();

  return repaired;
}

function transformTextBlock(block: any): SimpleItem[] {
  const data = getBlockData(block);
  if (!Array.isArray(data.content) || data.content.length === 0) return [];

  const content = repairTextNodes(data.content);
  const baseStyle = extractTextBlockStyle(data);

  if (content.length > 1) {
    const childItems = content
      .map((node) => slateNodeToTextChildItem(node, baseStyle))
      .filter((item): item is SimpleTextChildItem => item !== null);

    if (childItems.length > 1) {
      return [
        {
          text: childItems,
          ...(baseStyle ? { style: baseStyle } : {}),
        },
      ];
    }

    if (childItems.length === 1) {
      const [firstChild] = childItems;
      return firstChild
        ? [baseStyle ? applyTextBlockStyle(firstChild as SimpleItem, baseStyle) : firstChild]
        : [];
    }
  }

  return content.flatMap((node) => {
    const style = mergeTextStyles(baseStyle, extractSlateNodeTextStyle(node));

    if (node.type === 'bulleted-list' && isSimpleListNode(node, 'bulleted-list')) {
      const items = slateListNodeToSimpleTextItems(node);
      return items.length > 0 ? [applyTextBlockStyle({ bullets: items }, style)] : [];
    }

    if (node.type === 'numbered-list' && isSimpleListNode(node, 'numbered-list')) {
      const items = slateListNodeToSimpleTextItems(node);
      return items.length > 0 ? [applyTextBlockStyle({ numbered: items }, style)] : [];
    }

    if (node.type === 'todo-list' && isSimpleListNode(node, 'todo-list')) {
      const items = slateListNodeToSimpleTextItems(node);
      return items.length > 0 ? [applyTextBlockStyle({ todo: items }, style)] : [];
    }

    const simpleItem = nodeToSimpleTextItem(node, style);
    return simpleItem ? [simpleItem] : [];
  });
}

function isMeaninglessTableParagraph(node: SlateNode): boolean {
  return (
    node.type === 'paragraph' && isSimpleInlineChildren(node.children) && slateToText([node]) === ''
  );
}

function toEmbeddedTableCellBlock(node: SlateNode): SimpleTableEmbeddedBlock | null {
  const data = (node.data as Record<string, unknown>) || {};

  if (node.type === 'table-cell-image') {
    return {
      kind: 'table_cell_block',
      blockType: 'image',
      data: {
        image: typeof data.image === 'string' ? data.image : '',
        ...(typeof data.alt === 'string' ? { alt: data.alt } : {}),
        ...(typeof data.height === 'number' ? { height: data.height } : {}),
        ...(typeof data.width === 'string' || typeof data.width === 'number'
          ? { width: data.width }
          : {}),
        ...(data.style && typeof data.style === 'object'
          ? { style: data.style as Record<string, string> }
          : {}),
        ...(typeof data.maskShape === 'string' ? { maskShape: data.maskShape } : {}),
        ...(typeof data.fitToWidth === 'boolean' ? { fitToWidth: data.fitToWidth } : {}),
        ...(data.cropData && typeof data.cropData === 'object'
          ? { cropData: data.cropData as Record<string, unknown> }
          : {}),
      },
    };
  }

  if (node.type === 'table-cell-video') {
    return {
      kind: 'table_cell_block',
      blockType: 'video',
      data: {
        video: typeof data.video === 'string' ? data.video : '',
        ...(typeof data.platform === 'string' ? { platform: data.platform } : {}),
        ...(data.displayMode === 'player' || data.displayMode === 'card'
          ? { displayMode: data.displayMode }
          : {}),
        ...(typeof data.previewThumbnail === 'string'
          ? { previewThumbnail: data.previewThumbnail }
          : {}),
        ...(typeof data.previewTitle === 'string' ? { previewTitle: data.previewTitle } : {}),
        ...(typeof data.previewDescription === 'string'
          ? { previewDescription: data.previewDescription }
          : {}),
        ...(typeof data.previewWebsite === 'string' ? { previewWebsite: data.previewWebsite } : {}),
        ...(typeof data.shouldCalculateAspectRatio === 'boolean'
          ? { shouldCalculateAspectRatio: data.shouldCalculateAspectRatio }
          : {}),
      },
    };
  }

  if (node.type === 'table-cell-button') {
    const buttonStyle =
      data.style && typeof data.style === 'object'
        ? {
            align:
              typeof (data.style as Record<string, unknown>).justifyContent === 'string'
                ? justifyContentToAlignment(
                    (data.style as Record<string, unknown>).justifyContent as string,
                  )
                : undefined,
            color:
              typeof (data.style as Record<string, unknown>).color === 'string'
                ? (data.style as Record<string, unknown>).color
                : undefined,
            backgroundColor:
              typeof (data.style as Record<string, unknown>).backgroundColor === 'string'
                ? (data.style as Record<string, unknown>).backgroundColor
                : undefined,
            borderColor:
              typeof (data.style as Record<string, unknown>).borderColor === 'string'
                ? (data.style as Record<string, unknown>).borderColor
                : undefined,
          }
        : undefined;

    return {
      kind: 'table_cell_block',
      blockType: 'button',
      data: {
        label: typeof data.label === 'string' ? data.label : '',
        url: typeof data.url === 'string' ? data.url : '',
        ...(typeof data.fullWidth === 'boolean' ? { fullWidth: data.fullWidth } : {}),
        ...(data.variant === 'primary' || data.variant === 'secondary' || data.variant === 'outline'
          ? { variant: data.variant }
          : {}),
        ...(buttonStyle
          ? {
              style: pickDefined(buttonStyle) as {
                align?: 'left' | 'center' | 'right' | 'justify';
                color?: string;
                backgroundColor?: string;
                borderColor?: string;
              },
            }
          : {}),
      },
    };
  }

  return null;
}

function extractSingleEmbeddedTableCellBlock(
  content: SlateNode[],
): SimpleTableEmbeddedBlock | null {
  const meaningfulNodes = content.filter((node) => !isMeaninglessTableParagraph(node));
  if (meaningfulNodes.length !== 1) {
    return null;
  }

  const [firstMeaningfulNode] = meaningfulNodes;
  return firstMeaningfulNode ? toEmbeddedTableCellBlock(firstMeaningfulNode) : null;
}

function containsTableCellVoidNode(node: SlateNode): boolean {
  if (
    node.type === 'table-cell-image' ||
    node.type === 'table-cell-video' ||
    node.type === 'table-cell-button'
  ) {
    return true;
  }

  return Array.isArray(node.children)
    ? node.children.some((child) => containsTableCellVoidNode(child))
    : false;
}

function isPlainTextLeaf(node: SlateNode): boolean {
  return (
    node.text !== undefined &&
    !node.bold &&
    !node.italic &&
    !node.underline &&
    !node.code &&
    !node.color &&
    node.fontSize === undefined
  );
}

function isPlainInlineTextNode(node: SlateNode): boolean {
  if (node.type === 'mention' || node.type === 'link' || node.type === 'inline_field') {
    return false;
  }

  if (node.text !== undefined) {
    return isPlainTextLeaf(node);
  }

  return (
    Array.isArray(node.children) && node.children.every((child) => isPlainInlineTextNode(child))
  );
}

function isPlainParagraphContent(content: SlateNode[]): boolean {
  const [firstNode] = content;
  const children = firstNode?.children;
  return (
    content.length === 1 &&
    firstNode?.type === 'paragraph' &&
    !firstNode.align &&
    !firstNode.textStyle &&
    Array.isArray(children) &&
    isSimpleInlineChildren(children) &&
    children.every((child) => isPlainInlineTextNode(child))
  );
}

function slateCellContentToSimpleValue(content: SlateNode[]): any {
  const embeddedBlock = extractSingleEmbeddedTableCellBlock(content);
  if (embeddedBlock) {
    return embeddedBlock;
  }

  if (isPlainParagraphContent(content)) {
    return slateToText(content);
  }

  return slateContentToRich(content);
}

function transformTableCellValue(cell: any): any {
  if (typeof cell === 'string') return cell;
  if (typeof cell === 'number' || typeof cell === 'boolean') return String(cell);

  if (isSlateContent(cell)) {
    return slateCellContentToSimpleValue(cell);
  }

  if (cell && typeof cell === 'object' && 'content' in cell) {
    const normalizedContent = isSlateContent(cell.content)
      ? slateCellContentToSimpleValue(cell.content)
      : typeof cell.content === 'string' ||
          typeof cell.content === 'number' ||
          typeof cell.content === 'boolean'
        ? String(cell.content)
        : cell.content;
    const format =
      cell.format && typeof cell.format === 'object'
        ? pickDefined({
            bold: typeof cell.format.bold === 'boolean' ? cell.format.bold : undefined,
            italic: typeof cell.format.italic === 'boolean' ? cell.format.italic : undefined,
            underline:
              typeof cell.format.underline === 'boolean' ? cell.format.underline : undefined,
            color: typeof cell.format.color === 'string' ? cell.format.color : undefined,
            backgroundColor:
              typeof cell.format.backgroundColor === 'string'
                ? cell.format.backgroundColor
                : undefined,
            align: [cell.format.textAlign, cell.format.align].find(
              (value) => value === 'left' || value === 'center' || value === 'right',
            ),
          })
        : undefined;

    if (!format || Object.keys(format).length === 0) {
      return normalizedContent;
    }

    return {
      content: normalizedContent,
      format,
    };
  }

  return '';
}

/**
 * Transform image block to simple format
 */
function transformImageBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!data || Object.keys(data).length === 0) return null;

  // Handle multiple field names: image (from forward transformer), src, url
  const url = data.image || data.src || data.url;
  const alt = data.alt;
  const prompt = data.image_prompt || data.prompt;
  const align = justifyContentToAlignment(data.style?.justifyContent);
  const style = extractNonAlignmentBlockStyle(data.style);
  const extraImageData = pickDefined({
    url: url || undefined,
    alt: alt || undefined,
    // Without a URL the prompt is what the builder will generate from; with one it would
    // make the rebuilt block regenerate the image instead of keeping it.
    prompt: url ? undefined : prompt || undefined,
    image_search_query: data.image_search_query || undefined,
    height: typeof data.height === 'number' ? data.height : undefined,
    // The builder stores null for an automatic width; the agent schema has no null width.
    width: data.width ?? undefined,
    align,
    maskShape: data.maskShape || undefined,
    fitToWidth: typeof data.fitToWidth === 'boolean' ? data.fitToWidth : undefined,
    cropData: data.cropData || undefined,
    focusPoint: data.focusPoint || undefined,
    caption: data.caption || undefined,
    colorCode: data.colorCode || undefined,
    image_generation_request_id: data.image_generation_request_id || undefined,
    image_generation_status: data.image_generation_status || undefined,
    image_generation_error: data.image_generation_error || undefined,
    style,
  });

  // If only URL, use simple string format
  if (url && !alt && !prompt && Object.keys(extraImageData).length === 1) {
    return { image: url };
  }

  // If has additional properties, use object format
  if (url || prompt || Object.keys(extraImageData).length > 0) {
    return { image: extraImageData as any };
  }

  return null;
}

/**
 * Transform video block to simple format
 */
function transformVideoBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const src = data?.video || data?.src;
  if (!src) return null;

  return {
    video: pickDefined({
      src,
      platform: data.platform || undefined,
      title: data.title || undefined,
      description: data.description || undefined,
      align: justifyContentToAlignment(data.style?.justifyContent),
      height: typeof data.height === 'number' ? data.height : undefined,
      width: data.width,
      displayMode: data.displayMode || undefined,
      previewThumbnail: data.previewThumbnail || undefined,
      previewTitle: data.previewTitle || undefined,
      previewDescription: data.previewDescription || undefined,
      previewWebsite: data.previewWebsite || undefined,
      aspectRatio: typeof data.aspectRatio === 'number' ? data.aspectRatio : undefined,
      style: extractNonAlignmentBlockStyle(data.style),
    }) as any,
  };
}

/**
 * Transform embed_webpage block to simple format
 */
function transformEmbedWebpageBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const src = data?.video || data?.src;
  if (!src) return null;

  return {
    embed_webpage: pickDefined({
      src,
      platform: data.platform || undefined,
      title: data.title || undefined,
      description: data.description || undefined,
      align: justifyContentToAlignment(data.style?.justifyContent),
      height: typeof data.height === 'number' ? data.height : undefined,
      width: data.width,
      displayMode: data.displayMode || undefined,
      previewThumbnail: data.previewThumbnail || undefined,
      previewTitle: data.previewTitle || undefined,
      previewDescription: data.previewDescription || undefined,
      previewWebsite: data.previewWebsite || undefined,
      aspectRatio: typeof data.aspectRatio === 'number' ? data.aspectRatio : undefined,
      style: extractNonAlignmentBlockStyle(data.style),
    }) as any,
  };
}

/**
 * Transform button block to simple format
 */
function transformButtonBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const alignment = justifyContentToAlignment(data?.style?.justifyContent);

  if (Array.isArray(data?.buttons) && data.buttons.length > 0) {
    return {
      button: pickDefined({
        buttons: data.buttons.map((button: any) =>
          pickDefined({
            label: button.label,
            url: button.url || undefined,
            variant: button.variant || undefined,
            fullWidth: button.fullWidth,
            style:
              button.style && Object.keys(button.style).length > 0
                ? pickDefined({
                    align: justifyContentToAlignment(button.style.justifyContent),
                    width: button.style.width,
                    height: button.style.height,
                    borderRadius: button.style.borderRadius,
                    color: button.style.color,
                    backgroundColor: button.style.backgroundColor,
                    borderColor: button.style.borderColor,
                    borderWidth: button.style.borderWidth,
                    fontFamily: button.style.fontFamily,
                    fontWeight: button.style.fontWeight,
                  })
                : undefined,
          }),
        ),
        fullWidth: data.fullWidth,
        align: alignment,
      }) as any,
    };
  }

  if (!data?.label) return null;

  const style =
    data.style && Object.keys(data.style).length > 0
      ? pickDefined({
          align: justifyContentToAlignment(data.style.justifyContent),
          width: data.style.width,
          height: data.style.height,
          borderRadius: data.style.borderRadius,
          color: data.style.color,
          backgroundColor: data.style.backgroundColor,
          borderColor: data.style.borderColor,
          borderWidth: data.style.borderWidth,
          fontFamily: data.style.fontFamily,
          fontWeight: data.style.fontWeight,
        })
      : undefined;

  return {
    button: pickDefined({
      label: data.label,
      url: data.url || undefined,
      variant: data.variant || undefined,
      fullWidth: data.fullWidth,
      style: style && Object.keys(style).length > 0 ? style : undefined,
    }) as any,
  };
}

function transformIconBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!data || (!data.icon && !data.iconName)) return null;

  const icon = pickDefined({
    iconName: data.iconName || undefined,
    iconSvg: data.icon || undefined,
    width: typeof data.width === 'number' ? data.width : undefined,
    height: typeof data.height === 'number' ? data.height : undefined,
    iconColor: data.iconColor || undefined,
    align: justifyContentToAlignment(data.style?.justifyContent),
    style: extractNonAlignmentBlockStyle(data.style),
  });

  if (icon.iconName && Object.keys(icon).length === 1) {
    return { icon: icon.iconName };
  }

  return { icon: icon as any };
}

function transformCalloutBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const content = Array.isArray(data?.content) ? data.content : undefined;
  const plainText = slateToText(content);
  const hasRichContent =
    Array.isArray(content) &&
    (content.length > 1 ||
      !isPlainParagraphContent(content) ||
      content.some((node) => containsTableCellVoidNode(node)));
  if (!content && !data?.calloutStyle && !data?.customColor) return null;

  if (
    plainText &&
    !hasRichContent &&
    !data?.calloutStyle &&
    data?.hideIcon === undefined &&
    data?.bgOpacity === undefined &&
    data?.borderWidth === undefined &&
    !data?.customColor
  ) {
    return { callout_box: plainText };
  }

  return {
    callout_box: pickDefined({
      content: hasRichContent ? slateContentToRich(content || []) : plainText || undefined,
      calloutStyle: data?.calloutStyle || undefined,
      hideIcon: typeof data?.hideIcon === 'boolean' ? data.hideIcon : undefined,
      bgOpacity: typeof data?.bgOpacity === 'number' ? data.bgOpacity : undefined,
      borderWidth: typeof data?.borderWidth === 'number' ? data.borderWidth : undefined,
      customColor: data?.customColor || undefined,
    }) as any,
  };
}

function transformDividerBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!data || Object.keys(data).length === 0) {
    return { divider: true };
  }

  return {
    divider: pickDefined({
      style: data.style || undefined,
      thickness: typeof data.thickness === 'number' ? data.thickness : undefined,
      color: data.color || undefined,
      paddingTop: typeof data.paddingTop === 'number' ? data.paddingTop : undefined,
      paddingBottom: typeof data.paddingBottom === 'number' ? data.paddingBottom : undefined,
      label: data.label || undefined,
      showLabel: typeof data.showLabel === 'boolean' ? data.showLabel : undefined,
    }) as any,
  };
}

function transformLabelBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const labels = Array.isArray(data?.labels)
    ? data.labels.map((label: any) =>
        pickDefined({
          text: label.text,
          variant: label.variant || undefined,
          icon: label.icon || undefined,
          style:
            label.style && Object.keys(label.style).length > 0
              ? pickDefined({
                  backgroundColor: label.style.backgroundColor,
                  color: label.style.color,
                  borderColor: label.style.borderColor,
                })
              : undefined,
        }),
      )
    : [];

  if (labels.length === 0) return null;

  if (
    labels.length === 1 &&
    !data?.fullWidth &&
    !data?.style?.justifyContent &&
    labels[0]?.text &&
    Object.keys(labels[0]).length === 1
  ) {
    return { label: labels[0].text };
  }

  return {
    label: pickDefined({
      labels,
      fullWidth: typeof data?.fullWidth === 'boolean' ? data.fullWidth : undefined,
      align: justifyContentToAlignment(data?.style?.justifyContent),
    }) as any,
  };
}

function transformBlockQuoteBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!Array.isArray(data?.content) || data.content.length === 0) return null;
  const content = repairTextNodes(data.content);
  const quoteNode =
    content.length === 1 && (content[0]?.type === 'block-quote' || content[0]?.type === 'paragraph')
      ? content[0]
      : undefined;
  const style = mergeTextStyles(
    extractTextBlockStyle(data),
    quoteNode ? extractSlateNodeTextStyle(quoteNode) : undefined,
  );
  // A quote of several paragraphs, headings or list items is kept as one quote with line breaks.
  const quoteContent = slateChildrenToSimpleText(
    quoteNode ? quoteNode.children : flattenToInlineNodes(content),
  );

  if (quoteContent.length === 0) {
    return null;
  }

  if (!style) {
    return { block_quote: quoteContent };
  }

  return {
    block_quote: {
      content: quoteContent,
      style,
    },
  };
}

function transformCodeBlock(block: any): SimpleItem | null {
  const textItems = transformTextBlock(block);
  if (textItems.length === 0) return null;

  const firstItem = textItems[0];
  if (!firstItem) return null;

  let content: SimpleTextContent | undefined = undefined;
  let style: any = undefined;

  if ('p' in firstItem) {
    content = firstItem.p;
    style = firstItem.style;
  } else if ('h1' in firstItem) {
    content = firstItem.h1;
    style = firstItem.style;
  } else if ('h2' in firstItem) {
    content = firstItem.h2;
    style = firstItem.style;
  } else if ('h3' in firstItem) {
    content = firstItem.h3;
    style = firstItem.style;
  } else if ('h4' in firstItem) {
    content = firstItem.h4;
    style = firstItem.style;
  } else if ('h5' in firstItem) {
    content = firstItem.h5;
    style = firstItem.style;
  } else if ('bullets' in firstItem && firstItem.bullets && firstItem.bullets.length > 0) {
    content = listItemContent(firstItem.bullets[0]);
    style = firstItem.style;
  } else if ('numbered' in firstItem && firstItem.numbered && firstItem.numbered.length > 0) {
    content = listItemContent(firstItem.numbered[0]);
    style = firstItem.style;
  }

  if (!content) return null;

  const firstNode = content[0];
  const canSimplifyToString =
    content.length === 1 &&
    firstNode !== undefined &&
    'text' in firstNode &&
    Object.keys(firstNode).length === 1;

  if (!style) {
    if (canSimplifyToString && firstNode !== undefined && 'text' in firstNode) {
      return { code: firstNode.text };
    }
    return { code: content };
  }

  return {
    code: {
      content:
        canSimplifyToString && firstNode !== undefined && 'text' in firstNode
          ? firstNode.text
          : content,
      style,
    },
  };
}

/**
 * Transform chart block to simple format
 */
function transformChartBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!data || Object.keys(data).length === 0) return null;

  // Handle nested chartConfig structure from forward transformer
  const chartConfig = data.chartConfig;
  if (chartConfig) {
    const {
      labels = [],
      datasets: storedDatasets = [],
      ...extraData
    } = chartConfig.z_data || chartConfig.data || {};
    const datasets = storedDatasets.map((ds: any) =>
      pickDefined({
        label: ds.label || '',
        data: ds.z_data || ds.data || [],
        backgroundColor: ds.backgroundColor,
        borderColor: ds.borderColor,
        borderWidth: ds.borderWidth,
        fill: ds.fill,
        tension: ds.tension,
      }),
    );

    const { title: titleOption, ...otherPlugins } = chartConfig.options?.plugins ?? {};
    const titleIsPlain =
      titleOption &&
      Object.keys(titleOption).every((key) => key === 'text' || key === 'display') &&
      titleOption.display !== false;
    const plugins = {
      ...otherPlugins,
      ...(titleOption && !titleIsPlain ? { title: titleOption } : {}),
    };
    const { plugins: _plugins, ...otherOptions } = chartConfig.options ?? {};
    const {
      type,
      data: _data,
      options: _options,
      style,
      uuid: _uuid,
      z_data: _zData,
      ...otherConfig
    } = chartConfig;

    const config = pickDefined({
      options: nonEmptyRecord({
        ...otherOptions,
        ...(nonEmptyRecord(plugins) ? { plugins } : {}),
      }),
      style: JSON.stringify(style) === JSON.stringify(DEFAULT_CHART_STYLE) ? undefined : style,
      data: nonEmptyRecord(extraData),
      ...otherConfig,
    });

    return {
      chart: pickDefined({
        // The builder has no 'doughnut' chart; 'donut' is the one it draws as a ring.
        type: type === 'doughnut' ? 'donut' : type || 'bar',
        title: titleOption?.text || undefined,
        labels,
        datasets,
        width: typeof data.width === 'number' ? data.width : undefined,
        height: typeof data.height === 'number' ? data.height : undefined,
        align: justifyContentToAlignment(data.style?.justifyContent),
        config: nonEmptyRecord(config),
      }) as any,
    };
  }

  // Fallback to flat structure (legacy format)
  return {
    chart: {
      type: data.type || 'bar',
      title: data.title || undefined,
      labels: data.labels || [],
      datasets: data.datasets || [],
    },
  };
}

/**
 * Transform table block to simple format
 */
function transformTableBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  if (!data) return null;
  const tableRows = Array.isArray(data.table)
    ? data.table
    : Array.isArray(data.rows)
      ? data.rows.map((row: any) => row?.cells || [])
      : null;

  if (!tableRows) return null;

  const convertedRows = tableRows.map((row: any) =>
    Array.isArray(row) ? row.map((cell: any) => transformTableCellValue(cell)) : [],
  );
  // Simple tables are rectangular; a short row gets empty trailing cells.
  const columnCount = Math.max(0, ...convertedRows.map((row: unknown[]) => row.length));
  const rows = convertedRows.map((row: unknown[]) =>
    row.length < columnCount ? [...row, ...Array<string>(columnCount - row.length).fill('')] : row,
  );
  const columnWidths = Array.isArray(data.columnWidths)
    ? data.columnWidths.filter((width: unknown) => typeof width === 'number')
    : undefined;
  const style = extractCommonBlockStyle(data.style);
  const canUseShorthand =
    !columnWidths &&
    !style &&
    rows.every(
      (row: unknown) =>
        Array.isArray(row) && row.every((cell: unknown) => typeof cell === 'string'),
    );

  if (canUseShorthand) {
    return { table: rows as string[][] };
  }

  return {
    table: pickDefined({
      rows,
      column_widths: columnWidths,
      style,
    }) as any,
  };
}

/**
 * Transform pricing table block to simple format
 */
function transformPricingTableBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const rawTable = data?.table;
  if (!rawTable || typeof rawTable !== 'object' || Array.isArray(rawTable)) {
    // An empty block still occupies a content slot. Returning null hides it from the agent,
    // which shifts every later content index and makes targeted edits land on the wrong block.
    return {
      pricing_table: completeSemanticPricingTable({
        columns: [],
        rows: [],
      }),
    } as SimpleItem;
  }

  const table = rawTable as any;
  const asRecord = (value: unknown): Record<string, unknown> | null =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : null;
  const toSimpleFormat = (value: unknown): SimpleTableCellFormat | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
    const format = value as Record<string, unknown>;
    return pickDefined({
      bold: typeof format.bold === 'boolean' ? format.bold : undefined,
      italic: typeof format.italic === 'boolean' ? format.italic : undefined,
      underline: typeof format.underline === 'boolean' ? format.underline : undefined,
      color: typeof format.color === 'string' ? format.color : undefined,
      backgroundColor:
        typeof format.backgroundColor === 'string' ? format.backgroundColor : undefined,
      align:
        format.textAlign === 'left' || format.textAlign === 'center' || format.textAlign === 'right'
          ? format.textAlign
          : undefined,
    });
  };
  const getColumnFormat = (role: SimplePricingColumnRole, format: unknown) => {
    if (role === 'item' || role === 'text' || role === 'quantity') return null;
    if (role === 'unit_price' || role === 'line_total') return 'currency' as const;
    return format === 'currency' ? 'currency' : 'percentage';
  };
  const isSemantic =
    table.schemaVersion === 2 && Array.isArray(table.columns) && Array.isArray(table.rows);
  const columns = isSemantic
    ? table.columns.map((column: unknown) => {
        const columnRecord = asRecord(column);
        const role = columnRecord?.role as SimplePricingColumnRole;
        return {
          label: typeof columnRecord?.label === 'string' ? columnRecord.label : '',
          role,
          format: getColumnFormat(role, columnRecord?.format),
        };
      })
    : (Array.isArray(table.tableData?.[0]) ? table.tableData[0] : []).map(
        (cell: unknown, index: number, header: unknown[]) => {
          const role = (
            index === 0
              ? 'item'
              : index === 1
                ? 'unit_price'
                : index === header.length - 2
                  ? 'quantity'
                  : index === header.length - 1
                    ? 'line_total'
                    : (table.columnTypes?.[index] ?? 'text')
          ) as SimplePricingColumnRole;
          return {
            label: typeof cell === 'string' ? cell : '',
            role,
            format: getColumnFormat(role, table.columnFormats?.[index]),
          };
        },
      );
  const rows = isSemantic
    ? table.rows.map((row: unknown) => {
        const rowRecord = asRecord(row);
        const cells = asRecord(rowRecord?.cells);
        return columns.map((_: unknown, columnIndex: number) => {
          const columnId = table.columns[columnIndex]?.id;
          const value = typeof columnId === 'string' ? cells?.[columnId] : '';
          return typeof value === 'string' ? value : '';
        });
      })
    : (Array.isArray(table.tableData) ? table.tableData.slice(1) : []).map((row: unknown) =>
        Array.isArray(row) ? row.map((cell: unknown) => tableCellToSimpleText(cell)) : [],
      );
  const footerRows = Array.isArray(table.footerRows)
    ? table.footerRows.map((row: unknown) => {
        const rowRecord = asRecord(row);
        return {
          label: typeof rowRecord?.label === 'string' ? rowRecord.label : '',
          value:
            typeof rowRecord?.value === 'string' && rowRecord.value.trim() ? rowRecord.value : '0',
          type:
            rowRecord?.type === 'discount' || rowRecord?.type === 'tax' || rowRecord?.type === 'fee'
              ? rowRecord.type
              : null,
          format:
            rowRecord?.format === 'percentage' || rowRecord?.format === 'currency'
              ? rowRecord.format
              : null,
        };
      })
    : [];
  const semanticColumnWidths = isSemantic
    ? table.columns.map((column: unknown) => asRecord(column)?.width)
    : [];
  const hasUsableSemanticWidths =
    isSemantic &&
    semanticColumnWidths.length === columns.length &&
    semanticColumnWidths.every(
      (width: unknown): width is number =>
        typeof width === 'number' && Number.isFinite(width) && width > 0,
    );
  const legacyColumnWidths = Array.isArray(table.columnWidths)
    ? table.columnWidths.filter(
        (width: unknown): width is number =>
          typeof width === 'number' && Number.isFinite(width) && width > 0,
      )
    : [];
  const columnWidths = hasUsableSemanticWidths
    ? semanticColumnWidths
    : legacyColumnWidths.length === columns.length
      ? legacyColumnWidths
      : undefined;
  const headerFormat =
    isSemantic && table.headerFormat
      ? toSimpleFormat(table.headerFormat)
      : Array.isArray(table.rowCellFormats) && table.rowCellFormats[0]
        ? toSimpleFormat(table.rowCellFormats[0])
        : undefined;
  const rowCellFormats =
    isSemantic && table.rows.some((row: unknown) => asRecord(row)?.format)
      ? table.rows.map((row: unknown) => toSimpleFormat(asRecord(row)?.format) ?? {})
      : Array.isArray(table.rowCellFormats)
        ? table.rowCellFormats.slice(1).map(toSimpleFormat)
        : undefined;
  const columnCellFormats =
    isSemantic && table.columns.some((column: unknown) => asRecord(column)?.cellFormat)
      ? table.columns.map((column: unknown) => toSimpleFormat(asRecord(column)?.cellFormat) ?? {})
      : Array.isArray(table.columnCellFormats)
        ? table.columnCellFormats.map(toSimpleFormat)
        : undefined;

  return {
    pricing_table: completeSemanticPricingTable({
      columns,
      rows,
      ...(columnWidths ? { column_widths: columnWidths } : {}),
      ...(headerFormat ? { header_format: headerFormat } : {}),
      ...(rowCellFormats ? { row_cell_formats: rowCellFormats } : {}),
      ...(columnCellFormats ? { column_cell_formats: columnCellFormats } : {}),
      ...(footerRows.length > 0 ? { footer_rows: footerRows } : {}),
    }),
  } as SimpleItem;
}

/**
 * Transform quotation builder block to simple format
 */
function transformQuotationBuilderBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  // Quotation builder is an interactive block, return with data if available
  if (data && Object.keys(data).length > 0) {
    return { quotation_builder: data };
  }
  return { quotation_builder: true };
}

/**
 * Transform space block to simple format
 */
function transformSpaceBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const height =
    typeof data.style?.height === 'number'
      ? data.style.height
      : typeof data.height === 'number'
        ? data.height
        : DEFAULT_SPACE_HEIGHT;
  return { space: height };
}

/**
 * Transform page break to simple format
 */
function transformPageBreakBlock(): SimpleItem {
  return { page_break: true };
}

/**
 * Transform table of contents to simple format
 */
function transformTocBlock(block: any): SimpleItem {
  const data = getBlockData(block);
  const style = extractCommonBlockStyle(data?.style);

  if (
    !data ||
    Object.keys(
      pickDefined({
        include_heading_levels:
          data.includeHeadingLevels && typeof data.includeHeadingLevels === 'object'
            ? data.includeHeadingLevels
            : undefined,
        show_page_numbers:
          typeof data.showPageNumbers === 'boolean' ? data.showPageNumbers : undefined,
        page_number_alignment:
          data.pageNumberAlignment === 'left' || data.pageNumberAlignment === 'right'
            ? data.pageNumberAlignment
            : undefined,
        style,
      }),
    ).length === 0
  ) {
    return { table_of_contents: true };
  }

  return {
    table_of_contents: pickDefined({
      include_heading_levels:
        data.includeHeadingLevels && typeof data.includeHeadingLevels === 'object'
          ? data.includeHeadingLevels
          : undefined,
      show_page_numbers:
        typeof data.showPageNumbers === 'boolean' ? data.showPageNumbers : undefined,
      page_number_alignment:
        data.pageNumberAlignment === 'left' || data.pageNumberAlignment === 'right'
          ? data.pageNumberAlignment
          : undefined,
      style,
    }) as any,
  };
}

// ============================================================================
// FILLABLE BLOCK TRANSFORMERS (document flow blocks)
// ============================================================================

/**
 * Transform signature_block to simple format
 */
function transformSignatureBlockReverse(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const placeholder = data?.placeholder;
  const textPlaceholder = data?.signature_text_placeholder;
  const align = blockStyleAlignment(data?.style);

  const isRecipientField = data?.is_recipient_field;

  if (isRecipientField === false) {
    // Editor signature block
    if (placeholder || textPlaceholder || align) {
      return {
        editor_signature_block: {
          placeholder: placeholder || undefined,
          text_placeholder: textPlaceholder || undefined,
          align: align || undefined,
        },
      };
    }
    return { editor_signature_block: true };
  }

  // Recipient signature block (default)
  if (placeholder || textPlaceholder || align) {
    return {
      recipient_signature_block: {
        placeholder: placeholder || undefined,
        text_placeholder: textPlaceholder || undefined,
        align: align || undefined,
      },
    };
  }

  return { recipient_signature_block: true };
}

/**
 * Transform initials_block to simple format
 */
function transformInitialsBlockReverse(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const placeholder = data?.placeholder;
  const align = blockStyleAlignment(data?.style);
  const isRecipientField = data?.is_recipient_field;

  if (isRecipientField === false) {
    if (placeholder || align) {
      return { editor_initials_block: { placeholder, align: align || undefined } };
    }
    return { editor_initials_block: true };
  }

  if (placeholder || align) {
    return { recipient_initials_block: { placeholder, align: align || undefined } };
  }

  return { recipient_initials_block: true };
}

/**
 * Transform stamp_block to simple format
 */
function transformStampBlockReverse(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const placeholder = data?.placeholder;
  const align = blockStyleAlignment(data?.style);
  const isRecipientField = data?.is_recipient_field;

  if (isRecipientField === false) {
    if (placeholder || align) {
      return { editor_stamp_block: { placeholder, align: align || undefined } };
    }
    return { editor_stamp_block: true };
  }

  if (placeholder || align) {
    return { recipient_stamp_block: { placeholder, align: align || undefined } };
  }

  return { recipient_stamp_block: true };
}

/**
 * Transform fillable_text block to simple format
 */
function transformFillableTextBlockReverse(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const content = data?.content;
  const baseStyle = extractTextBlockStyle(data);
  const isRecipientField = data?.is_recipient_field;

  if (!Array.isArray(content) || content.length === 0) {
    return isRecipientField === false ? { editor_fillable_text: [] } : { fillable_text: [] };
  }

  const firstNode = content[0];
  const childItems = content
    .map((node) => slateNodeToFillableChildItem(node, baseStyle))
    .filter((item): item is SimpleFillableChildItem => item !== null);

  if (isRecipientField === false) {
    if (
      content.length === 1 &&
      firstNode &&
      (!firstNode.type || firstNode.type === 'paragraph') &&
      isSimpleFillableInlineChildren(firstNode.children) &&
      !firstNode.align &&
      !baseStyle
    ) {
      return { editor_fillable_text: slateChildrenToSimpleFillableText(firstNode.children) };
    }

    if (childItems.length === 1 && !baseStyle) {
      const [firstChild] = childItems;
      if (firstChild && 'p' in firstChild && !('style' in firstChild && firstChild.style)) {
        return { editor_fillable_text: firstChild.p || [] };
      }
    }

    return {
      editor_fillable_text: {
        content: childItems,
        ...(baseStyle ? { style: baseStyle } : {}),
      },
    };
  }

  if (
    content.length === 1 &&
    firstNode &&
    (!firstNode.type || firstNode.type === 'paragraph') &&
    isSimpleFillableInlineChildren(firstNode.children) &&
    !firstNode.align &&
    !baseStyle
  ) {
    return { fillable_text: slateChildrenToSimpleFillableText(firstNode.children) };
  }

  if (childItems.length === 1 && !baseStyle) {
    const [firstChild] = childItems;
    if (firstChild && 'p' in firstChild && !('style' in firstChild && firstChild.style)) {
      return { fillable_text: firstChild.p || [] };
    }
  }

  return {
    fillable_text: {
      content: childItems,
      ...(baseStyle ? { style: baseStyle } : {}),
    },
  };
}

// ============================================================================
// SMART BLOCK TRANSFORMERS
// ============================================================================

/**
 * Extract smart block items from data
 */
function extractSmartBlockItems(data: any): SimpleSmartBlockItem[] {
  const items = getBlockItems(data);
  return items.map((item: any) => ({
    title: slateValueToSmartTextContent(item.title),
    description: slateValueToSmartTextContent(item.description),
    icon: item.icon || undefined,
    color:
      themeColorTokenFromCss(item.color) ||
      themeColorTokenFromCss(item.textColor) ||
      item.color ||
      item.textColor ||
      undefined,
    bgColor: themeColorTokenFromCss(item.bgColor) || item.bgColor || undefined,
  }));
}

function buildSmartBlockConfig(data: any): Record<string, any> {
  return pickDefined({
    layoutId: data?.layoutId || undefined,
    style:
      data?.style && Object.keys(data.style).length > 0
        ? pickDefined({
            color: data.style.color,
          })
        : undefined,
  });
}

/**
 * Transform timeline block to simple format
 */
function transformTimelineBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const viewMode = data?.viewMode;
  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
    viewMode: viewMode && viewMode !== 'vertical' ? viewMode : undefined,
  });

  if (Object.keys(config).length > 1) {
    return { timeline: config } as SimpleItem;
  }
  return { timeline: items };
}

type ProcessFlowColumns = 'single' | 'two' | 'three';

function resolveProcessFlowColumns(data: any): ProcessFlowColumns | undefined {
  const layoutColumns: Record<string, ProcessFlowColumns> = {
    'process-flow-single': 'single',
    'process-flow-two': 'two',
    'process-flow-three': 'three',
  };
  const columnsFromLayout = layoutColumns[data?.layoutId];
  if (columnsFromLayout) return columnsFromLayout;

  return data?.columns === 'single' || data?.columns === 'two' || data?.columns === 'three'
    ? data.columns
    : undefined;
}

/**
 * Transform process flow block to simple format
 */
function transformProcessFlowBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
    columns: resolveProcessFlowColumns(data),
  });

  if (Object.keys(config).length > 1) {
    return { process_flow: config } as SimpleItem;
  }
  return { process_flow: items };
}

/**
 * Transform steps block to simple format
 */
function transformStepsBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
  });
  return Object.keys(config).length > 1 ? ({ steps: config } as SimpleItem) : { steps: items };
}

/**
 * Transform arrow block to simple format
 */
function transformArrowBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
    direction: data?.direction === 'bottom' ? 'bottom' : undefined,
  });
  return Object.keys(config).length > 1 ? ({ arrow: config } as SimpleItem) : { arrow: items };
}

/**
 * Transform staircase block to simple format
 */
function transformStaircaseBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
  });
  return Object.keys(config).length > 1
    ? ({ staircase: config } as SimpleItem)
    : { staircase: items };
}

/**
 * Transform pyramid block to simple format
 */
function transformPyramidBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const defaultOrientation = block.type === 'funnel' ? 'down' : 'up';
  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
    orientation:
      data?.orientation && data.orientation !== defaultOrientation ? data.orientation : undefined,
    textInside: typeof data?.textInside === 'boolean' ? data.textInside : undefined,
  });

  if (Object.keys(config).length > 1) {
    return { [block.type]: config } as SimpleItem;
  }
  return { [block.type]: items } as SimpleItem;
}

/**
 * Transform circular arrow block to simple format
 */
function transformCircularArrowBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
  });
  return Object.keys(config).length > 1
    ? ({ circular_arrow: config } as SimpleItem)
    : { circular_arrow: items };
}

function transformColumnSmartBlock(block: any): SimpleItem | null {
  const data = getBlockData(block);
  const items = extractSmartBlockItems(data);
  if (items.length === 0) return null;

  const config = pickDefined({
    items,
    ...buildSmartBlockConfig(data),
    columns: data?.columns || undefined,
    align: data?.textAlign || undefined,
    orientation: data?.orientation || undefined,
  });

  return Object.keys(config).length > 1
    ? ({ [block.type]: config } as SimpleItem)
    : ({ [block.type]: items } as SimpleItem);
}

// ============================================================================
// CONTENT BLOCK TRANSFORMER
// ============================================================================

/**
 * Transform content block to simple format
 */
function transformContentBlock(block: any): SimpleItem | null {
  const type = block.type as SimpleContentBlockType;
  if (!CONTENT_BLOCK_TYPES.includes(type)) return null;

  const data = getBlockData(block);
  const layout = data?.layout;
  const items = getBlockItems(data);

  if (layout || items.length > 0) {
    const options: { layout?: string; items?: string[] } = {};
    if (layout) {
      options.layout = layout;
    }
    if (items.length > 0) {
      options.items = items;
    }
    return { [type]: options } as SimpleItem;
  }

  return { [type]: true } as SimpleItem;
}

// ============================================================================
// LAYOUT TRANSFORMER
// ============================================================================

/**
 * Transform row to simple column layout
 * Uses shared helpers for z_items/items and z_data/data compatibility
 */
function transformRow(row: any): SimpleItem | null {
  const columns = getBlockItems(row);
  if (columns.length === 0) return null;

  const columnContents: SimpleItem[][] = columns.map((col: any) => {
    if (col?.type === 'column') {
      return transformBuilderItems(getBlockItems(col));
    }

    return transformBuilderItemToItems(col);
  });

  const rowData = getBlockData(row);
  const widths = rowData?.widths || row.widths;
  const normalizedWidths =
    Array.isArray(widths) && widths.length === columnContents.length ? widths : undefined;

  return {
    columns: columnContents,
    widths: normalizedWidths,
  };
}

/**
 * Transform builder items while unwrapping standalone column containers.
 * Some runtime payloads store page/root content inside nested `column` nodes
 * rather than directly under the root column.
 */
function transformBuilderItems(items: any[]): SimpleItem[] {
  const content: SimpleItem[] = [];

  for (const item of items) {
    content.push(...transformBuilderItemToItems(item));
  }

  return content;
}

function simpleTextContentToPlainText(content: SimpleTextContent): string {
  return content
    .map((node) => {
      if ('text' in node) return node.text;
      if ('var' in node) return `[[${node.var}]]`;
      return simpleTextContentToPlainText(node.content);
    })
    .join('')
    .trim();
}

function getFirstHeadingFromSimpleItems(items: SimpleItem[]): string | null {
  for (const item of items) {
    if ('h1' in item && item.h1) return simpleTextContentToPlainText(item.h1);
    if ('h2' in item && item.h2) return simpleTextContentToPlainText(item.h2);
    if ('h3' in item && item.h3) return simpleTextContentToPlainText(item.h3);

    if ('columns' in item) {
      for (const column of item.columns) {
        const heading = getFirstHeadingFromSimpleItems(column);
        if (heading) {
          return heading.slice(0, 50); // Limit to 50 characters for page name
        }
      }
    }
  }

  return null;
}

function transformBlockToItems(block: any): SimpleItem[] {
  if (!block || !block.type) return [];

  switch (block.type) {
    case 'text':
    case 'text_heading_one':
    case 'text_heading_two':
    case 'text_heading_three':
      return transformTextBlock(block);
    case 'image': {
      const transformed = transformImageBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'video': {
      const transformed = transformVideoBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'embed_webpage': {
      const transformed = transformEmbedWebpageBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'button': {
      const transformed = transformButtonBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'icon': {
      const transformed = transformIconBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'callout_box': {
      const transformed = transformCalloutBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'divider': {
      const transformed = transformDividerBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'label': {
      const transformed = transformLabelBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'block_quote': {
      const transformed = transformBlockQuoteBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'code': {
      const transformed = transformCodeBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'bulleted_list': {
      const transformed = transformTextBlock(block);
      return transformed ? transformed.map((item) => ('bullets' in item ? item : item)) : [];
    }
    case 'numbered_list': {
      const transformed = transformTextBlock(block);
      return transformed ? transformed.map((item) => ('numbered' in item ? item : item)) : [];
    }
    case 'todo_list': {
      const transformed = transformTextBlock(block);
      return transformed ? transformed.map((item) => ('todo' in item ? item : item)) : [];
    }
    case 'chart': {
      const transformed = transformChartBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'table': {
      const transformed = transformTableBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'pricing_table': {
      const transformed = transformPricingTableBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'quotation_builder': {
      const transformed = transformQuotationBuilderBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'space': {
      const transformed = transformSpaceBlock(block);
      return transformed ? [transformed] : [];
    }
    case 'page_break':
      return [transformPageBreakBlock()];
    case 'table_of_contents':
      return [transformTocBlock(block)];
    case 'signature_block': {
      const transformed = transformSignatureBlockReverse(block);
      return transformed ? [transformed] : [];
    }
    case 'initials_block': {
      const transformed = transformInitialsBlockReverse(block);
      return transformed ? [transformed] : [];
    }
    case 'stamp_block': {
      const transformed = transformStampBlockReverse(block);
      return transformed ? [transformed] : [];
    }
    case 'fillable_text': {
      const transformed = transformFillableTextBlockReverse(block);
      return transformed ? [transformed] : [];
    }
    default:
      console.warn(`Unknown block type: ${block.type}`);
      return [];
  }
}

function transformBuilderItemToItems(item: any): SimpleItem[] {
  if (!item || !item.type) return [];

  switch (item.type) {
    case 'block':
      return transformBlockToItems(item.block);
    case 'smart_block': {
      const transformed = transformSmartBlock(item.block);
      return transformed ? [transformed] : [];
    }
    case 'content_block': {
      const transformed = transformContentBlock(item.block);
      return transformed ? [transformed] : [];
    }
    case 'row': {
      const transformed = transformRow(item);
      return transformed ? [transformed] : [];
    }
    case 'column':
      return transformBuilderItems(getBlockItems(item));
    default:
      console.warn(`Unknown item type: ${item.type}`);
      return [];
  }
}

// ============================================================================
// MAIN ITEM TRANSFORMER
// ============================================================================

/**
 * Transform a single Builder item to Simple format
 */
function transformBuilderItem(item: any): SimpleItem | null {
  const items = transformBuilderItemToItems(item);
  return items[0] || null;
}

/**
 * Transform a regular block
 */
function transformBlock(block: any): SimpleItem | null {
  const items = transformBlockToItems(block);
  return items[0] || null;
}

/**
 * Transform a smart block
 */
function transformSmartBlock(block: any): SimpleItem | null {
  if (!block || !block.type) return null;

  switch (block.type) {
    case 'timeline':
      return transformTimelineBlock(block);
    case 'process_flow':
      return transformProcessFlowBlock(block);
    case 'steps':
      return transformStepsBlock(block);
    case 'arrow':
      return transformArrowBlock(block);
    case 'staircase':
      return transformStaircaseBlock(block);
    case 'pyramid':
    case 'funnel':
      return transformPyramidBlock(block);
    case 'circular_arrow':
      return transformCircularArrowBlock(block);
    case 'icon_text':
    case 'top_circle_boxes':
    case 'solid_boxes':
    case 'side_line_boxes':
    case 'quote_boxes':
      return transformColumnSmartBlock(block);
    default:
      console.warn(`Unknown smart block type: ${block.type}`);
      return null;
  }
}

// ============================================================================
// PAGE TRANSFORMER
// ============================================================================

/**
 * Extract the SimplePage shell from a Builder page without content.
 * This is useful when callers need custom content serialization.
 */
export function builderPageToSimpleShell(
  page: any,
  fallbackPageName?: string | null,
): Omit<SimplePage, 'content'> | null {
  if (!page) return null;

  const simplePageShell: Omit<SimplePage, 'content'> = {
    name: (page.name === DEFAULT_PAGE_NAME ? fallbackPageName : null) || page.name || 'Untitled',
  };

  const pageData = page.data || {};

  // An explicit value is a real override in either direction, so report it as stored. Only a
  // missing value means the page follows the document.
  if (typeof pageData.isLandscape === 'boolean') {
    simplePageShell.isLandscape = pageData.isLandscape;
  }
  const pageLayout = pageData.pageLayout || page.pageLayout;
  const cardBg = pageData.cardBackground || page.cardBackground;
  const appearance: SimplePageAppearance = {};

  if (typeof pageData.visibility === 'boolean') {
    appearance.visibility = pageData.visibility;
  }

  if (cardBg && typeof cardBg === 'object' && !Array.isArray(cardBg)) {
    const cardAppearance = extractCardAppearance(cardBg as Record<string, unknown>);
    if (cardAppearance) {
      appearance.card = cardAppearance;
    }
  }

  if (pageLayout && ['top', 'left', 'right', 'background'].includes(pageLayout)) {
    const layout: NonNullable<SimplePageAppearance['layout']> = {
      mode: pageLayout,
    };
    const cardBackground =
      cardBg && typeof cardBg === 'object' && !Array.isArray(cardBg) ? cardBg : {};
    const layoutSize =
      pageData.layoutSize &&
      typeof pageData.layoutSize === 'object' &&
      !Array.isArray(pageData.layoutSize)
        ? (pageData.layoutSize as Record<string, unknown>)
        : {};

    const size: NonNullable<NonNullable<SimplePageAppearance['layout']>['size']> = {};
    if (pageLayout === 'top') {
      const topSize =
        layoutSize.top && typeof layoutSize.top === 'object' && !Array.isArray(layoutSize.top)
          ? (layoutSize.top as Record<string, unknown>)
          : {};
      if (typeof topSize.height === 'number') {
        size.height = topSize.height;
      }
    }
    if (pageLayout === 'left' || pageLayout === 'right') {
      const sideSize =
        layoutSize.side && typeof layoutSize.side === 'object' && !Array.isArray(layoutSize.side)
          ? (layoutSize.side as Record<string, unknown>)
          : layoutSize[pageLayout] && typeof layoutSize[pageLayout] === 'object'
            ? (layoutSize[pageLayout] as Record<string, unknown>)
            : {};
      if (typeof sideSize.width === 'number') {
        size.width = sideSize.width;
      }
    }
    if (Object.keys(size).length > 0) {
      layout.size = size;
    }

    const media: NonNullable<NonNullable<SimplePageAppearance['layout']>['media']> = {};
    const layoutImage = extractLayoutImage(cardBackground as Record<string, unknown>);
    if (layoutImage) {
      media.image = layoutImage;
    }
    const mediaType = (cardBackground as Record<string, unknown>).mediaType;
    if (mediaType === 'video' || mediaType === 'embed') {
      media.type = mediaType;
    }
    if (
      (pageLayout === 'top' || pageLayout === 'left' || pageLayout === 'right') &&
      isNonEmptyString((cardBackground as Record<string, unknown>).accentColor)
    ) {
      media.fill = {
        mode:
          (cardBackground as Record<string, unknown>).accentType === 'gradient'
            ? 'gradient'
            : 'solid',
        value:
          themeColorTokenFromCss(
            (cardBackground as Record<string, unknown>).accentColor as string,
          ) || ((cardBackground as Record<string, unknown>).accentColor as string),
      };
    } else if (
      (pageLayout === 'top' || pageLayout === 'left' || pageLayout === 'right') &&
      !media.image &&
      (cardBackground as Record<string, unknown>).useThemeCardColor !== true &&
      isNonEmptyString((cardBackground as Record<string, unknown>).colorCode)
    ) {
      media.fill = {
        mode: inferFillMode((cardBackground as Record<string, unknown>).colorCode as string),
        value:
          themeColorTokenFromCss((cardBackground as Record<string, unknown>).colorCode as string) ||
          ((cardBackground as Record<string, unknown>).colorCode as string),
      };
    }
    if (
      (cardBackground as Record<string, unknown>).fitMode === 'contain' ||
      (cardBackground as Record<string, unknown>).fitMode === 'cover'
    ) {
      media.fit = (cardBackground as Record<string, unknown>).fitMode as 'contain' | 'cover';
    }
    const focusPoint = (cardBackground as Record<string, unknown>).focusPoint;
    if (
      focusPoint &&
      typeof focusPoint === 'object' &&
      !Array.isArray(focusPoint) &&
      typeof (focusPoint as Record<string, unknown>).x === 'number' &&
      typeof (focusPoint as Record<string, unknown>).y === 'number'
    ) {
      media.focus = {
        x: (focusPoint as Record<string, unknown>).x as number,
        y: (focusPoint as Record<string, unknown>).y as number,
      };
    }
    if (Object.keys(media).length > 0) {
      layout.media = media;
    }

    if (
      pageLayout === 'background' &&
      ((cardBackground as Record<string, unknown>).overlayEffect === 'clear' ||
        (cardBackground as Record<string, unknown>).overlayEffect === 'faded' ||
        (cardBackground as Record<string, unknown>).overlayEffect === 'frosted' ||
        typeof (cardBackground as Record<string, unknown>).overlayIntensity === 'number' ||
        isNonEmptyString((cardBackground as Record<string, unknown>).overlayColor))
    ) {
      layout.overlay = {
        effect:
          ((cardBackground as Record<string, unknown>).overlayEffect as
            | 'clear'
            | 'faded'
            | 'frosted') || 'clear',
        ...(typeof (cardBackground as Record<string, unknown>).overlayIntensity === 'number'
          ? { intensity: (cardBackground as Record<string, unknown>).overlayIntensity as number }
          : {}),
        ...(typeof (cardBackground as Record<string, unknown>).overlayBlurIntensity === 'number'
          ? { blur: (cardBackground as Record<string, unknown>).overlayBlurIntensity as number }
          : {}),
        ...(isNonEmptyString((cardBackground as Record<string, unknown>).overlayColor)
          ? {
              color:
                themeColorTokenFromCss(
                  (cardBackground as Record<string, unknown>).overlayColor as string,
                ) || ((cardBackground as Record<string, unknown>).overlayColor as string),
            }
          : {}),
      };
    }

    appearance.layout = layout;
  }

  if (Object.keys(appearance).length > 0) {
    simplePageShell.appearance = appearance;
  }

  const headerFooterPreserve = extractHeaderFooterPreserve(pageData.headerFooter);
  if (headerFooterPreserve) {
    simplePageShell._preserve = {
      header_footer: headerFooterPreserve,
    };
  }

  return simplePageShell;
}

/**
 * Transform a Builder page to Simple page format
 */
function transformBuilderPage(page: any): SimplePage | null {
  if (!page || !page.root_node) return null;

  // Use shared helper for z_items/items compatibility
  const rootItems = getBlockItems(page.root_node);
  const content = transformBuilderItems(rootItems);

  const fallbackPageName = getFirstHeadingFromSimpleItems(content);
  const simplePageShell = builderPageToSimpleShell(page, fallbackPageName);

  if (!simplePageShell) {
    return null;
  }

  return {
    ...simplePageShell,
    content,
  };
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Convert Builder JSON document to Simple JSON format
 *
 * @param builderDoc - The complex Builder JSON document (array of pages)
 * @returns SimpleDocument - The simplified AI-friendly format
 *
 * @example
 * const simpleDoc = builderToSimpleJson(builderDocument);
 * // Now you can send simpleDoc to AI for editing
 * // Then convert back with simpleToBuilderJson(simpleDoc)
 */
export function builderToSimpleJson(builderDoc: any[]): SimpleDocument {
  if (!Array.isArray(builderDoc)) {
    console.warn('builderToSimpleJson: Expected array of pages');
    return [];
  }

  return builderDoc.map(transformBuilderPage).filter(Boolean) as SimpleDocument;
}

if (typeof window !== 'undefined') {
  (window as any).builderToSimpleJson = builderToSimpleJson;
}

/**
 * Convert a single Builder page to Simple page format
 */
export function builderPageToSimple(page: any): SimplePage | null {
  return transformBuilderPage(page);
}

/**
 * Convert a single Builder item to Simple item format
 */
export function builderItemToSimpleItems(item: any): SimpleItem[] {
  return transformBuilderItemToItems(item);
}

/**
 * Convert a single Builder item to the first matching Simple item format.
 * For mixed text blocks that expand into multiple simple items, use
 * `builderItemToSimpleItems` instead.
 */
export function builderItemToSimple(item: any): SimpleItem | null {
  return transformBuilderItem(item);
}
